import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  type ReactNode,
} from "react";

import { Provider, atom, createStore } from "jotai";

import { useUser } from "@components/user-context";
import { getRandomNbColor } from "@features/mmm/components/explore-budgets/budget-mix-chart/color-helper";
import { useMMMAdminMode } from "@features/mmm/components/explore-budgets/budget-mix-table/utils";
import {
  type ClientSetting,
  type DataColumn,
  type Kpi,
  type ModelMetadata,
} from "@features/mmm/components/explore-budgets/client-setting/types";
import { useNorthbeamLazyQuery, useNorthbeamQuery } from "@hooks/http";
import {
  type FetchBlessedBudgetMixesForCycle,
  type FetchUniversalMMMData_me_mmm_availableBudgetMixTypes,
  type FetchUniversalMMMData_me_mmm_alerts,
} from "@north-beam/nb-client-side";
import {
  type BudgetMixData,
  MMMClientState,
  type MMMClientCycle,
  type MMMClientCycleBase,
} from "@north-beam/nb-common";

import { BUDGET_MIXES_FOR_CYCLE_QUERY } from "./explore-budgets-v2/explore-budgets-queries";
import { useBudgetMixes } from "./hooks/use-budget-mixes";
import { useSelectedCycle } from "./hooks/use-selected-cycle";

export type BudgetMixOption = Omit<
  FetchUniversalMMMData_me_mmm_availableBudgetMixTypes,
  "__typename"
>;

const InitialClientSettings: ClientSetting[] = [];
const InitialClientSettingsColorMap: Record<string, string> = {};
export const InitialClientSelectedCycle: MMMClientCycle = {
  id: "-1",
  date_range: {
    id: -1,
    start_date: "05-06-1990",
    end_date: "05-06-1990",
  },
  model: {
    id: -1,
    is_blessed: false,
    model_path: "",
  },
  state: MMMClientState.NOT_SET,
  is_current: false,
  is_errored: true,
  is_manually_overridden: false,
  breakdowns: ["Platform (Northbeam)", "Category (Northbeam)"],
  cadenceInDays: 35,
  clientSettings: [],
  hidden: false,
  name: "",
  cycleInputStage: null,
};
const InitialClientCycles: MMMClientCycleBase[] = [];
const InitialClientDataColumns: DataColumn[] = [];
const InitialClientKpis: Kpi[] = [];
const InitialBudgetMixes: BudgetMixData = [];
const InitialAvailableBudgetMixOptions: BudgetMixOption[] = [];

type AdoptedMixData = {
  id: number;
  clientSettingId?: string;
};
const InitialAdoptedMixData: AdoptedMixData | null = null;

const InitialAlerts: FetchUniversalMMMData_me_mmm_alerts[] = [];

const _ClientSettingColorAtom = atom<Record<string, string>>(
  InitialClientSettingsColorMap,
);
const _ClientSettingsAtom = atom<ClientSetting[]>(InitialClientSettings);

export const ClientActiveSettingsAtom = atom<ClientSetting[]>(
  InitialClientSettings,
);

export const ClientCyclesAtom = atom<MMMClientCycleBase[]>(InitialClientCycles);

export const AlertsAtom =
  atom<FetchUniversalMMMData_me_mmm_alerts[]>(InitialAlerts);

export const ClientLegacyDataColumnsAtom = atom<DataColumn[]>(
  InitialClientDataColumns,
);
export const ClientDataColumnsAtom = atom<DataColumn[]>(
  InitialClientDataColumns,
);
const InitialClientCadenceAtom = 35;
export const ClientCadenceAtom = atom<number>(InitialClientCadenceAtom);
export const ClientKpisAtom = atom<Kpi[]>(InitialClientKpis);
export const ModelMetadataAtom = atom<ModelMetadata | null>(null);

export const BudgetMixesAtom = atom<BudgetMixData>(InitialBudgetMixes);

export type MMMClient = {
  id: string;
  input_google_sheet_url: string;
};

export const MMMClientAtom = atom<MMMClient | null>(null);

type SetFromPreviousCallback = (prev: ClientSetting[]) => ClientSetting[];
export const ClientSettingsAtom = atom(
  (get) => get(_ClientSettingsAtom),
  (get, set, arg: SetFromPreviousCallback | ClientSetting[]) => {
    const performUpdate = (clientSettings: ClientSetting[]) => {
      set(_ClientSettingsAtom, clientSettings);
      set(_ClientSettingColorAtom, (prevColorMap) => {
        const updatedColorMap: Record<string, string> = {};
        for (const clientSetting of clientSettings) {
          if (prevColorMap[clientSetting.id]) {
            updatedColorMap[clientSetting.id] = prevColorMap[clientSetting.id];
          }
        }

        const usedClientSettingColors = Object.values(updatedColorMap);
        const newColorMap: Record<string, string> = { ...updatedColorMap };
        clientSettings.forEach((setting) => {
          if (!newColorMap[setting.id]) {
            const filterOut = [
              ...usedClientSettingColors,
              ...Object.values(newColorMap),
            ];
            const color = getRandomNbColor(filterOut);
            newColorMap[setting.id] = color;
          }
        });
        return newColorMap;
      });
    };
    if (typeof arg === "function") {
      const prevClientSettings = get(_ClientSettingsAtom);
      const newClientSettings = (arg as SetFromPreviousCallback)(
        prevClientSettings,
      );
      performUpdate(newClientSettings);
    } else {
      performUpdate(arg);
    }
  },
);

export const ClientSettingColorAtom = atom((get) =>
  get(_ClientSettingColorAtom),
);

export const AdoptedMixAtom = atom<AdoptedMixData | null>(
  InitialAdoptedMixData,
);

export const AvailableBudgetMixOptionsAtom = atom<BudgetMixOption[] | null>(
  InitialAvailableBudgetMixOptions,
);

export const InitialClientBreakdowns = [];
export const ClientBreakdownsAtom = atom<string[]>(InitialClientBreakdowns);

const GlobalMMMStore = (function () {
  const store = createStore();
  const resetStore = () => {
    store.set(_ClientSettingColorAtom, InitialClientSettingsColorMap);
    store.set(_ClientSettingsAtom, InitialClientSettings);
    store.set(ClientCyclesAtom, InitialClientCycles);
    store.set(ClientLegacyDataColumnsAtom, InitialClientDataColumns);
    store.set(ClientDataColumnsAtom, InitialClientDataColumns);
    store.set(ClientKpisAtom, InitialClientKpis);
    store.set(AdoptedMixAtom, InitialAdoptedMixData);
    store.set(ClientBreakdownsAtom, InitialClientBreakdowns);
    store.set(ClientActiveSettingsAtom, InitialClientSettings);
    store.set(ClientCadenceAtom, InitialClientCadenceAtom);
    store.set(AvailableBudgetMixOptionsAtom, InitialAvailableBudgetMixOptions);
    store.set(AlertsAtom, InitialAlerts);
  };
  return {
    store,
    resetStore,
  };
})();

const _useGlobalMMMStore = () => {
  const store = GlobalMMMStore.store;

  const resetGlobalMMMStore = () => {
    GlobalMMMStore.resetStore();
  };

  return [store, resetGlobalMMMStore] as const;
};

export const useGlobalMMMStore = () => {
  const [globalMMMStore] = _useGlobalMMMStore();
  return globalMMMStore;
};

export interface MMMContextType {
  isBudgetMixDataLoading: boolean;
  loadBudgetMixData: () => Promise<void>;
}

const MMMContext = createContext<MMMContextType>({
  isBudgetMixDataLoading: false,
  loadBudgetMixData: () => Promise.resolve(),
});

export const useMMM = () => {
  const context = useContext(MMMContext);
  if (context === undefined) {
    throw new Error("useMMM must be used within a MmmProvider");
  }
  return context;
};

export const MmmProvider = ({
  children,
}: {
  children: ReactNode[] | ReactNode;
}) => {
  const [globalMMMStore, resetGlobalMMMStore] = _useGlobalMMMStore();
  useEffect(() => {
    return () => {
      resetGlobalMMMStore();
    };
  }, []);

  const { selectedCycle } = useSelectedCycle();
  const [, setBudgetMixes] = useBudgetMixes();
  const { user } = useUser();
  const isAdminMode = useMMMAdminMode(user);

  const { loading: isBudgetMixDataLoading, data } =
    useNorthbeamQuery<FetchBlessedBudgetMixesForCycle>(
      BUDGET_MIXES_FOR_CYCLE_QUERY,
      {
        variables: {
          id: selectedCycle.id,
          blessed: !isAdminMode,
        },

        skip: selectedCycle.id === "-1",
      },
    );

  const {
    loadData: loadBudgetMixDataNoCache,
    data: { loading: isBudgetMixDataNoCacheLoading, data: lazyData },
  } = useNorthbeamLazyQuery<FetchBlessedBudgetMixesForCycle>(
    BUDGET_MIXES_FOR_CYCLE_QUERY,
    {
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (data) {
      setBudgetMixes(data.me.mmm.cycle.budgetMixes as BudgetMixData);
    }
  }, [data, setBudgetMixes]);

  useEffect(() => {
    if (lazyData) {
      setBudgetMixes(lazyData.me.mmm.cycle.budgetMixes as BudgetMixData);
    }
  }, [lazyData, setBudgetMixes]);

  const loadBudgetMixData = useCallback(
    async () =>
      loadBudgetMixDataNoCache({
        variables: {
          id: selectedCycle.id,
          blessed: !isAdminMode,
        },
      }),
    [loadBudgetMixDataNoCache, selectedCycle.id, isAdminMode],
  );

  return (
    <MMMContext.Provider
      value={{
        isBudgetMixDataLoading:
          isBudgetMixDataLoading || isBudgetMixDataNoCacheLoading,
        loadBudgetMixData,
      }}
    >
      <Provider store={globalMMMStore}>{children}</Provider>
    </MMMContext.Provider>
  );
};
